/**
 * EditVetContactsForm (SPEC 2026-10-26 AC 6.2, admin half) — the desk edits
 * a dog's vets on the Pet Profile: the regular vet, and a second practice
 * "if the regular vet is closed" (#552, carried over from #616). Nothing
 * here is ever required: a walk-in is never turned away over a phone number
 * nobody has on them, and an owner's "vet before the first booking" setting
 * asks the CUSTOMER online, never the desk.
 *
 * PATCHes /api/admin/dogs/:dogId/vet with only the fields that changed
 * (a blank box clears one), then asks the page to refetch, so what renders
 * is always the server's value, not optimistic local state.
 *
 * Five states: default · submitting (disabled) · error (the server's own
 * sentence; the typing is kept, so a retry costs nothing) · empty
 * (placeholders invite the first entry) · success (closes). An older server
 * that has no such route says so in plain words and the page hides the
 * affordance (`onUnavailable`).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  isRouteNotMapped,
  updateDogVetContacts,
  type DogVetContacts,
  type DogVetContactsPatch,
} from '../../lib/api.js';
import './EditVetContactsForm.css';

interface EditVetContactsFormProps {
  apiUrl: string;
  dogId: string;
  /** What is on file now (null = nothing). */
  value: DogVetContacts;
  onSaved: () => void;
  onCancel: () => void;
  /** The server predates the route: the page stops offering the edit. */
  onUnavailable: () => void;
}

type FieldKey = keyof DogVetContacts;

const REGULAR: ReadonlyArray<{ key: FieldKey; label: string; type: 'text' | 'tel' | 'email'; max: number }> = [
  { key: 'vetName', label: 'Vet or clinic name', type: 'text', max: 200 },
  { key: 'vetPhone', label: 'Phone', type: 'tel', max: 20 },
  { key: 'vetEmail', label: 'Email', type: 'email', max: 254 },
  { key: 'vetAddress', label: 'Address', type: 'text', max: 500 },
];

const SECONDARY: ReadonlyArray<{ key: FieldKey; label: string; type: 'text' | 'tel' | 'email'; max: number }> = [
  { key: 'secondaryVetName', label: 'Vet or clinic name', type: 'text', max: 200 },
  { key: 'secondaryVetPhone', label: 'Phone', type: 'tel', max: 20 },
  { key: 'secondaryVetEmail', label: 'Email', type: 'email', max: 254 },
  { key: 'secondaryVetAddress', label: 'Address', type: 'text', max: 500 },
];

export const SECOND_VET_LABEL = 'Second vet (if the regular vet is closed)';

function toText(value: DogVetContacts): Record<FieldKey, string> {
  return {
    vetName: value.vetName ?? '',
    vetPhone: value.vetPhone ?? '',
    vetAddress: value.vetAddress ?? '',
    vetEmail: value.vetEmail ?? '',
    secondaryVetName: value.secondaryVetName ?? '',
    secondaryVetPhone: value.secondaryVetPhone ?? '',
    secondaryVetAddress: value.secondaryVetAddress ?? '',
    secondaryVetEmail: value.secondaryVetEmail ?? '',
  };
}

/** Only the fields whose trimmed text differs from what is on file; '' clears. */
export function vetPatchFrom(current: DogVetContacts, text: Record<FieldKey, string>): DogVetContactsPatch {
  const patch: DogVetContactsPatch = {};
  const before = toText(current);
  for (const key of Object.keys(before) as FieldKey[]) {
    const next = text[key].trim();
    if (next !== before[key].trim()) patch[key] = next;
  }
  return patch;
}

export function EditVetContactsForm({
  apiUrl,
  dogId,
  value,
  onSaved,
  onCancel,
  onUnavailable,
}: EditVetContactsFormProps): JSX.Element {
  const [text, setText] = useState<Record<FieldKey, string>>(() => toText(value));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const patch = vetPatchFrom(value, text);
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await updateDogVetContacts(apiUrl, dogId, patch);
    if (res.data) {
      onSaved();
      return;
    }
    setSubmitting(false);
    if (isRouteNotMapped(res)) {
      setError('Editing vet details is available after the next server update.');
      onUnavailable();
      return;
    }
    const detail = Array.isArray(res.details)
      ? (res.details as Array<{ message?: unknown }>).find((d) => typeof d?.message === 'string')
      : undefined;
    setError(
      res.error === 'Validation failed' && detail && typeof detail.message === 'string'
        ? detail.message
        : (res.error ?? "We couldn't save the vet details. Try again in a moment."),
    );
  };

  const field = (
    f: { key: FieldKey; label: string; type: 'text' | 'tel' | 'email'; max: number },
    index: number,
  ): JSX.Element => (
    <div className="ferro-dogprofile__vet-field" key={f.key}>
      <label className="ferro-dogprofile__field-label" htmlFor={`dogprofile-vet-${f.key}`}>
        {f.label}
      </label>
      <input
        id={`dogprofile-vet-${f.key}`}
        ref={index === 0 && f.key === 'vetName' ? firstRef : undefined}
        className="ferro-dogprofile__input"
        type={f.type}
        inputMode={f.type === 'tel' ? 'tel' : f.type === 'email' ? 'email' : undefined}
        autoComplete="off"
        maxLength={f.max}
        value={text[f.key]}
        disabled={submitting}
        onChange={(e) => setText((t) => ({ ...t, [f.key]: e.target.value }))}
        data-testid={`dogprofile-vet-input-${f.key}`}
      />
    </div>
  );

  return (
    <form className="ferro-dogprofile__note-form" onSubmit={onSubmit} noValidate data-testid="dogprofile-vet-form">
      <fieldset className="ferro-dogprofile__vet-fieldset">
        <legend className="ferro-dogprofile__subtitle">Regular vet</legend>
        {REGULAR.map(field)}
      </fieldset>
      <fieldset className="ferro-dogprofile__vet-fieldset" data-testid="dogprofile-vet-secondary-fields">
        <legend className="ferro-dogprofile__subtitle">{SECOND_VET_LABEL}</legend>
        {SECONDARY.map(field)}
      </fieldset>
      <p className="ferro-dogprofile__field-hint">
        None of these are required. Leave a box empty to remove what is there.
      </p>
      {error ? (
        <p className="ferro-dogprofile__form-error" role="alert" data-testid="dogprofile-vet-error">
          {error}
        </p>
      ) : null}
      <div className="ferro-dogprofile__note-actions">
        <button
          type="submit"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--primary"
          disabled={submitting}
          data-testid="dogprofile-vet-save"
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
